import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import useAutoResizeToParent from './useAutoResizeToParent'
import { formatPhone } from '../../utils/phone'

/**
 * Custom Trip Request embed (CTR) — account-level concierge intake form.
 *
 * A slick standalone form a reseller drops on their own site (iframe) or shares
 * as a direct link. Keyed by the account's CustomTripEmbedKey. On submit it
 * lands in Custom Trips as a Status='requested' quote. No auth — uses a plain
 * fetch against the public API. Auto-resizes to the host iframe.
 */
const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
const base = (key) => `${apiOrigin}/api/public/custom-trip/${encodeURIComponent(key)}`
const num = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : null }
const emailOk = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(s || ''))

export default function CustomTripRequestEmbed() {
  useAutoResizeToParent()
  const { key } = useParams()

  const [ctx, setCtx] = useState(null)
  const [ctxErr, setCtxErr] = useState('')
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    arriveDate: '', departDate: '', destination: '', golfers: '',
    rooms: '', beds: '', hotelStyle: 'central', roundsPerGolfer: '', preferredTime: '', notes: '', website: '',
  })
  const [dest, setDest] = useState({ lat: null, lng: null, label: '', status: '' }) // '', loading, ok, notfound, error
  const [places, setPlaces] = useState([])        // typeahead suggestions [{lat,lng,label}]
  const [placesOpen, setPlacesOpen] = useState(false)
  const [placesLoading, setPlacesLoading] = useState(false)
  const searchTimer = useRef(null)
  const [courses, setCourses] = useState([])
  const [rounds, setRounds] = useState([])   // [{ coursesId, date }]
  const [extra, setExtra] = useState([])     // [{ name, email }]
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(null)

  useEffect(() => {
    fetch(`${base(key)}/context`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setCtx)
      .catch(() => setCtxErr('This request form is currently unavailable.'))
  }, [key])

  const brand = ctx?.brand || {}
  const accent = brand.primaryColor || '#0f766e'
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Type-ahead: debounced place search so the customer PICKS an exact
  // city/region ("Cavendish, Prince Edward Island" vs "…, Alberta") instead of
  // us guessing from a bare name and landing on the wrong one.
  function onDestChange(v) {
    set('destination', v)
    setDest({ lat: null, lng: null, label: '', status: '' }) // picking again clears the confirmed place
    setCourses([])
    if (searchTimer.current) clearTimeout(searchTimer.current)
    const q = v.trim()
    if (q.length < 3) { setPlaces([]); setPlacesOpen(false); return }
    searchTimer.current = setTimeout(async () => {
      setPlacesLoading(true); setPlacesOpen(true)
      try {
        const r = await fetch(`${base(key)}/places?q=${encodeURIComponent(q)}`).then(x => x.json())
        setPlaces(r.results || [])
      } catch { setPlaces([]) }
      finally { setPlacesLoading(false) }
    }, 350)
  }

  async function pickPlace(p) {
    setPlacesOpen(false); setPlaces([])
    set('destination', p.label)
    setDest({ lat: p.lat, lng: p.lng, label: p.label, status: 'loading' })
    try {
      const cr = await fetch(`${base(key)}/courses?lat=${p.lat}&lng=${p.lng}`).then(x => x.json())
      setCourses(cr.courses || [])
      setDest({ lat: p.lat, lng: p.lng, label: p.label, status: 'ok' })
    } catch { setDest({ lat: p.lat, lng: p.lng, label: p.label, status: 'error' }) }
  }

  async function submit() {
    if (!form.firstName.trim()) return setError('Please enter your first name.')
    if (!emailOk(form.email)) return setError('Please enter a valid email address.')
    setError(''); setSubmitting(true)
    try {
      const body = {
        firstName: form.firstName, lastName: form.lastName, phone: form.phone, email: form.email,
        arriveDate: form.arriveDate || null, departDate: form.departDate || null,
        destination: form.destination || null, destLat: dest.lat, destLng: dest.lng,
        golfers: num(form.golfers), rooms: num(form.rooms), beds: num(form.beds), hotelStyle: form.hotelStyle,
        roundsPerGolfer: num(form.roundsPerGolfer), preferredTime: form.preferredTime || null,
        roundPicks: rounds.filter(r => r.coursesId).map(r => ({ coursesId: Number(r.coursesId), date: r.date || null })),
        extraGolfers: extra.filter(g => (g.name || '').trim() || (g.email || '').trim()),
        notes: form.notes || null,
        website: form.website, // honeypot
      }
      const r = await fetch(`${base(key)}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) return setError(j.message || 'Could not send your request. Please try again.')
      setDone(j.quoteNumber || true)
      try { window.scrollTo(0, 0) } catch {}
    } catch { setError('Could not send your request. Please try again.') }
    finally { setSubmitting(false) }
  }

  if (ctxErr) return <Shell accent="#0f766e"><div style={S.note}>{ctxErr}</div></Shell>
  if (!ctx) return <Shell accent="#0f766e"><div style={S.note}>Loading…</div></Shell>

  if (done) return (
    <Shell accent={accent} brand={brand} name={ctx.accountName}>
      <div style={{ ...S.card, textAlign: 'center', padding: '40px 24px' }}>
        <div style={{ fontSize: 42, lineHeight: 1 }}>✓</div>
        <h2 style={{ margin: '14px 0 8px', color: '#0f172a' }}>Request sent — thank you!</h2>
        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.6, maxWidth: 440, margin: '0 auto' }}>
          {ctx.accountName ? `${ctx.accountName} has` : "We've"} received your custom trip request{typeof done === 'string' ? ` (${done})` : ''}
          {' '}and will be in touch by email at <strong>{form.email}</strong> to build your quote.
        </p>
      </div>
    </Shell>
  )

  return (
    <Shell accent={accent} brand={brand} name={ctx.accountName}>
      {/* Honeypot — hidden from humans; bots fill it. */}
      <input type="text" name="website" value={form.website} onChange={e => set('website', e.target.value)}
        tabIndex={-1} autoComplete="off" aria-hidden style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

      <Section title="Your details" accent={accent}>
        <Row>
          <Field label="First name" req><input style={S.inp} value={form.firstName} onChange={e => set('firstName', e.target.value)} /></Field>
          <Field label="Last name"><input style={S.inp} value={form.lastName} onChange={e => set('lastName', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Email" req><input style={S.inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" /></Field>
          <Field label="Phone"><input style={S.inp} value={form.phone} onChange={e => set('phone', formatPhone(e.target.value))} placeholder="(902) 555 - 0100" /></Field>
        </Row>
      </Section>

      <Section title="Your trip" accent={accent}>
        <Row>
          <Field label="Arrive"><input style={S.inp} type="date" value={form.arriveDate} onChange={e => set('arriveDate', e.target.value)} /></Field>
          <Field label="Depart"><input style={S.inp} type="date" min={form.arriveDate || undefined} value={form.departDate} onChange={e => set('departDate', e.target.value)} /></Field>
          <Field label="Number of golfers"><input style={S.inp} type="number" min="1" max="64" value={form.golfers} onChange={e => set('golfers', e.target.value)} /></Field>
        </Row>
        <Field label="Destination (city / region)">
          <div style={{ position: 'relative' }}>
            <input style={S.inp} value={form.destination}
              onChange={e => onDestChange(e.target.value)}
              onFocus={() => { if (places.length) setPlacesOpen(true) }}
              onBlur={() => setTimeout(() => setPlacesOpen(false), 150)}
              autoComplete="off"
              placeholder="Start typing a city — e.g. Cavendish, PEI" />
            {placesOpen && (placesLoading || places.length > 0) && (
              <div style={S.dropdown}>
                {placesLoading && <div style={S.optMuted}>Searching…</div>}
                {!placesLoading && places.map((p, i) => (
                  <div key={i} onMouseDown={e => { e.preventDefault(); pickPlace(p) }} style={S.opt}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                    <span style={{ marginRight: 8 }}>📍</span>{p.label}
                  </div>
                ))}
                {!placesLoading && places.length === 0 && <div style={S.optMuted}>No matches — check spelling or add a note below.</div>}
              </div>
            )}
          </div>
          {dest.status === 'loading' && <div style={S.hint}>Finding courses near there…</div>}
          {dest.status === 'ok' && <div style={{ ...S.hint, color: '#15803d' }}>✓ {dest.label} — {courses.length} course{courses.length === 1 ? '' : 's'} within {ctx.radiusKm} km.</div>}
          {dest.status === 'error' && <div style={{ ...S.hint, color: '#b45309' }}>Couldn't load courses for that place — please try another.</div>}
          {dest.status !== 'ok' && <div style={S.hint}>Include the province/state (e.g. “Cavendish, PEI”) and pick from the list so we find the right place.</div>}
        </Field>
      </Section>

      <Section title="Hotel" accent={accent}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Hotel plan</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['central', 'Stay in one central hotel'], ['multiple', 'Stay in multiple hotels']].map(([val, label]) => {
              const on = form.hotelStyle === val
              return (
                <button key={val} type="button" onClick={() => set('hotelStyle', val)}
                  style={{ flex: '1 1 200px', padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${on ? accent : '#cbd5e1'}`, background: on ? `${accent}14` : '#fff', color: on ? accent : '#334155', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ marginRight: 8 }}>{on ? '●' : '○'}</span>{label}
                </button>
              )
            })}
          </div>
        </div>
        <Row>
          <Field label="Number of rooms"><input style={S.inp} type="number" min="1" max="40" value={form.rooms} onChange={e => set('rooms', e.target.value)} /></Field>
          <Field label="Number of overall beds"><input style={S.inp} type="number" min="1" max="80" value={form.beds} onChange={e => set('beds', e.target.value)} /></Field>
        </Row>
      </Section>

      <Section title="Golf" accent={accent}>
        <Row>
          <Field label="Rounds per golfer"><input style={S.inp} type="number" min="1" max="20" value={form.roundsPerGolfer} onChange={e => set('roundsPerGolfer', e.target.value)} /></Field>
          <Field label="Preferred start time">
            <select style={S.inp} value={form.preferredTime} onChange={e => set('preferredTime', e.target.value)}>
              <option value="">No preference</option>
              {(ctx.timeWindows || []).map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </Field>
        </Row>

        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Courses &amp; dates <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></div>
          {dest.status !== 'ok'
            ? <div style={S.hint}>Enter your destination above to choose from courses in the area.</div>
            : (
              <>
                {rounds.map((rd, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select style={{ ...S.inp, flex: '1 1 200px' }} value={rd.coursesId}
                      onChange={e => setRounds(rs => rs.map((r, j) => j === i ? { ...r, coursesId: e.target.value } : r))}>
                      <option value="">Choose a course…</option>
                      {courses.map(c => <option key={c.coursesId} value={c.coursesId}>{c.name}{c.city ? ` — ${c.city}` : ''} ({c.distanceKm} km)</option>)}
                    </select>
                    <input style={{ ...S.inp, flex: '0 1 170px' }} type="date"
                      min={form.arriveDate || undefined} max={form.departDate || undefined}
                      value={rd.date} onChange={e => setRounds(rs => rs.map((r, j) => j === i ? { ...r, date: e.target.value } : r))} />
                    <button type="button" onClick={() => setRounds(rs => rs.filter((_, j) => j !== i))} style={S.btnX} title="Remove">✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => setRounds(rs => [...rs, { coursesId: '', date: '' }])} style={{ ...S.btnGhost, borderColor: accent, color: accent }}>+ Add a round</button>
              </>
            )}
        </div>
      </Section>

      <Section title="Additional golfers" accent={accent} sub="Optional — add anyone else in your group.">
        {extra.map((g, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...S.inp, flex: '1 1 150px' }} placeholder="Name" value={g.name} onChange={e => setExtra(xs => xs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
            <input style={{ ...S.inp, flex: '1 1 180px' }} type="email" placeholder="Email" value={g.email} onChange={e => setExtra(xs => xs.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
            <button type="button" onClick={() => setExtra(xs => xs.filter((_, j) => j !== i))} style={S.btnX} title="Remove">✕</button>
          </div>
        ))}
        <button type="button" onClick={() => setExtra(xs => [...xs, { name: '', email: '' }])} style={{ ...S.btnGhost, borderColor: accent, color: accent }}>+ Add golfer</button>
      </Section>

      <Section title="Anything else?" accent={accent}>
        <textarea style={{ ...S.inp, minHeight: 84, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Budget, must-play courses, room preferences, special occasions…" />
      </Section>

      {error && <div style={S.err}>{error}</div>}
      <button type="button" onClick={submit} disabled={submitting}
        style={{ ...S.submit, background: accent, opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Sending…' : 'Send my trip request'}
      </button>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11.5, color: '#94a3b8' }}>Powered by InteGolf</div>
    </Shell>
  )
}

function Shell({ children, accent, brand = {}, name }) {
  return (
    <div style={{ background: '#f8fafc', minHeight: 1, padding: '20px 14px', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          {brand.logoUrl
            ? <img src={brand.logoUrl} alt="" style={{ height: 40, width: 'auto', maxWidth: 160, objectFit: 'contain' }} />
            : <div style={{ width: 8, height: 34, borderRadius: 4, background: accent }} />}
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Plan a custom golf trip</div>
            {name && <div style={{ fontSize: 13, color: '#64748b' }}>with {name}</div>}
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}

function Section({ title, sub, accent, children }) {
  return (
    <div style={S.card}>
      <div style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
      {children}
    </div>
  )
}
function Row({ children }) { return <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{children}</div> }
function Field({ label, req, children }) {
  return (
    <label style={{ display: 'block', flex: '1 1 160px', marginBottom: 10 }}>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}{req && <span style={{ color: '#dc2626' }}> *</span>}</span>
      {children}
    </label>
  )
}

const S = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' },
  inp: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff' },
  hint: { fontSize: 12.5, color: '#64748b', marginTop: 6 },
  note: { ...{}, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, textAlign: 'center', color: '#64748b' },
  btnGhost: { padding: '8px 14px', background: '#fff', border: '1px solid', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  dropdown: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.12)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' },
  opt: { padding: '10px 12px', fontSize: 14, color: '#0f172a', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' },
  optMuted: { padding: '10px 12px', fontSize: 13, color: '#94a3b8' },
  btnX: { border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 16, padding: '4px 6px' },
  submit: { width: '100%', padding: '13px 16px', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  err: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, marginBottom: 12 },
}
